import { useCallback, useEffect, useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import { LEADS_LIMITS, type LeadFunnelConfigPublic } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/contexts/confirmContext';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';
import { leadsApi } from '../api/leadsApi';
import { FunnelConfigWizard } from '../components/FunnelConfigWizard';
import { CampaignRoutingCard } from '../components/CampaignRoutingCard';
import {
  LeadsFunnelLegacyBanner,
  type LeadsFunnelStatus,
} from '../components/LeadsFunnelLegacyBanner';
import { formatDateTime24h } from '../utils/dates';

type FunnelPreview = {
  schemaVersion: number | null;
  verticalCount: number;
  verticalLabels: string[];
  scoringCut: number | null;
  intakeSources: string[];
};

type VerticalStatus = {
  key: string;
  label: string;
  enabled: boolean;
};

function statusVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'ACTIVE') {
    return 'default';
  }
  if (status === 'DRAFT') {
    return 'secondary';
  }
  return 'outline';
}

function extractPreview(payload: Record<string, unknown>): FunnelPreview {
  const verticals = payload.verticals;
  const verticalKeys =
    verticals && typeof verticals === 'object' && !Array.isArray(verticals)
      ? Object.keys(verticals as Record<string, unknown>)
      : [];
  const verticalLabels = verticalKeys.map(key => {
    const entry = (verticals as Record<string, { label?: string }>)[key];
    return entry?.label ? `${key} (${entry.label})` : key;
  });
  const scoring =
    payload.scoring && typeof payload.scoring === 'object'
      ? (payload.scoring as { cut?: number })
      : null;
  const intake =
    payload.intake && typeof payload.intake === 'object'
      ? (payload.intake as { fieldMappings?: Record<string, unknown> })
      : null;
  const intakeSources = intake?.fieldMappings ? Object.keys(intake.fieldMappings) : [];

  return {
    schemaVersion: typeof payload.schemaVersion === 'number' ? payload.schemaVersion : null,
    verticalCount: verticalKeys.length,
    verticalLabels,
    scoringCut: typeof scoring?.cut === 'number' ? scoring.cut : null,
    intakeSources,
  };
}

function extractVerticalStatuses(payload: Record<string, unknown> | null): VerticalStatus[] {
  if (!payload?.verticals || typeof payload.verticals !== 'object' || Array.isArray(payload.verticals)) {
    return [];
  }
  return Object.entries(payload.verticals as Record<string, unknown>).flatMap(([key, raw]) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return [];
    }
    const vertical = raw as { label?: unknown; enabled?: unknown };
    return [{
      key,
      label: typeof vertical.label === 'string' ? vertical.label : key,
      enabled: vertical.enabled !== false,
    }];
  });
}

function extractCampaignRoutes(payload: Record<string, unknown> | null): Array<{ key: string; vertical: string }> {
  if (!payload?.routing || typeof payload.routing !== 'object' || Array.isArray(payload.routing)) {
    return [];
  }
  const routes = (payload.routing as { verticalRoutes?: unknown }).verticalRoutes;
  if (!routes || typeof routes !== 'object' || Array.isArray(routes)) {
    return [];
  }
  const verticals =
    payload.verticals && typeof payload.verticals === 'object' && !Array.isArray(payload.verticals)
      ? payload.verticals as Record<string, { enabled?: boolean }>
      : {};
  return Object.entries(routes as Record<string, unknown>).flatMap(([key, vertical]) =>
    typeof vertical === 'string' && verticals[vertical]?.enabled !== false
      ? [{ key, vertical }]
      : [],
  );
}

function parseJsonPayload(raw: string): Record<string, unknown> {
  const bounded = raw.slice(0, LEADS_LIMITS.configPayloadMaxBytes + 1);
  if (bounded.length > LEADS_LIMITS.configPayloadMaxBytes) {
    throw new Error('El JSON supera el tamaño máximo permitido (512 KB).');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(bounded);
  } catch {
    throw new Error('El contenido no es JSON válido.');
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('La configuración debe ser un objeto JSON.');
  }
  return parsed as Record<string, unknown>;
}

export function LeadsFunnelConfigPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const { confirm } = useConfirm();
  const [configs, setConfigs] = useState<LeadFunnelConfigPublic[]>([]);
  const [jsonText, setJsonText] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activatingId, setActivatingId] = useState('');
  const [funnelStatus, setFunnelStatus] = useState<LeadsFunnelStatus | null>(null);
  const [activePayload, setActivePayload] = useState<Record<string, unknown> | null>(null);
  const [loadingActivePayload, setLoadingActivePayload] = useState(false);
  const [togglingVertical, setTogglingVertical] = useState('');

  const activeConfig = useMemo(() => configs.find(row => row.status === 'ACTIVE'), [configs]);
  const activeVerticals = useMemo(() => extractVerticalStatuses(activePayload), [activePayload]);
  const campaignRoutes = useMemo(() => extractCampaignRoutes(activePayload), [activePayload]);

  const loadConfigs = useCallback(async () => {
    const rows = await leadsApi.listFunnelConfigs();
    setConfigs(rows);
  }, []);

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void Promise.all([loadConfigs(), leadsApi.getFunnelStatus()])
      .then(([, status]) => {
        if (!cancelled) {
          setFunnelStatus(status);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId, loadConfigs]);

  useEffect(() => {
    if (!activeConfig?.id) {
      setActivePayload(null);
      return;
    }
    let cancelled = false;
    setLoadingActivePayload(true);
    void leadsApi.getFunnelConfig(activeConfig.id)
      .then(detail => {
        if (!cancelled) {
          setActivePayload(detail.payload);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingActivePayload(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeConfig?.id]);

  const preview = useMemo(() => {
    if (!jsonText.trim()) {
      return null;
    }
    try {
      return extractPreview(parseJsonPayload(jsonText));
    } catch {
      return null;
    }
  }, [jsonText]);

  const parseError = useMemo(() => {
    if (!jsonText.trim()) {
      return '';
    }
    try {
      parseJsonPayload(jsonText);
      return '';
    } catch (err) {
      return contactAgentError(err);
    }
  }, [jsonText]);

  const handleFile = async (file: File | null) => {
    if (!file) {
      return;
    }
    if (file.size > LEADS_LIMITS.configPayloadMaxBytes) {
      setError('El archivo supera el tamaño máximo permitido (512 KB).');
      return;
    }
    setError('');
    setMessage('');
    const text = await file.text();
    setJsonText(text);
  };

  const handleImport = async (applyNow: boolean) => {
    if (!jsonText.trim()) {
      setError('Pegá o subí un JSON de configuración.');
      return;
    }
    if (applyNow) {
      const accepted = await confirm({
        title: 'Aplicar configuración ahora',
        message: 'Se publica una versión nueva y el bot la usa desde el próximo mensaje. ¿Continuar?',
      });
      if (!accepted) {
        return;
      }
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const payload = parseJsonPayload(jsonText);
      if (applyNow) {
        const applied = await leadsApi.applyFunnelConfig(payload, note.trim() || undefined);
        setMessage(`Versión v${applied.version} aplicada. Ya está en uso.`);
      } else {
        const created = await leadsApi.importFunnelConfig(payload, note.trim() || undefined);
        setMessage(`Borrador v${created.version} importado. Activá la versión cuando esté lista.`);
      }
      setJsonText('');
      setNote('');
      await loadConfigs();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (config: LeadFunnelConfigPublic) => {
    const accepted = await confirm({
      title: 'Activar configuración',
      message: `La versión ${config.version} pasará a ACTIVE y la configuración activa actual quedará archivada. ¿Continuar?`,
    });
    if (!accepted) {
      return;
    }
    setActivatingId(config.id);
    setError('');
    setMessage('');
    try {
      await leadsApi.activateFunnelConfig(config.id);
      setMessage(`Versión ${config.version} activada.`);
      await loadConfigs();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setActivatingId('');
    }
  };

  const handleToggleVertical = async (vertical: VerticalStatus, enabled: boolean) => {
    if (!activePayload?.verticals || typeof activePayload.verticals !== 'object' || Array.isArray(activePayload.verticals)) {
      return;
    }
    const enabledCount = activeVerticals.filter(row => row.enabled).length;
    if (!enabled && vertical.enabled && enabledCount <= 1) {
      setError('El embudo debe conservar al menos una vertical habilitada.');
      return;
    }

    const nextPayload = structuredClone(activePayload);
    const verticals = nextPayload.verticals as Record<string, unknown>;
    const raw = verticals[vertical.key];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return;
    }
    verticals[vertical.key] = { ...(raw as Record<string, unknown>), enabled };

    setTogglingVertical(vertical.key);
    setError('');
    setMessage('');
    try {
      const applied = await leadsApi.applyFunnelConfig(
        nextPayload,
        `${enabled ? 'Habilitada' : 'Deshabilitada'} vertical ${vertical.key} desde administración`,
      );
      const detail = await leadsApi.getFunnelConfig(applied.id);
      setActivePayload(detail.payload);
      setMessage(`${vertical.label}: ${enabled ? 'habilitada' : 'deshabilitada'} en v${applied.version}.`);
      await loadConfigs();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setTogglingVertical('');
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Configuración del embudo'
        description='Usá el asistente visual para ajustar parámetros sin tocar JSON, o importá un archivo avanzado.'
        backTo='/admin/leads'
        eyebrow='Embudo de leads'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      <LeadsFunnelLegacyBanner status={funnelStatus} />

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}
      {message ? <p className='text-sm text-emerald-700 dark:text-emerald-400'>{message}</p> : null}

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Versión activa</CardTitle>
        </CardHeader>
        <CardContent>
          {activeConfig ? (
            <div className='flex flex-wrap items-center gap-2 text-sm'>
              <Badge>v{activeConfig.version}</Badge>
              <span className='text-muted-foreground'>
                Aplicada {activeConfig.appliedAt ? formatDateTime24h(activeConfig.appliedAt) : '—'}
              </span>
              {activeConfig.note ? <span className='text-muted-foreground'>· {activeConfig.note}</span> : null}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>No hay configuración activa para este tenant.</p>
          )}
        </CardContent>
      </Card>

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Verticales habilitadas</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-muted-foreground'>
            Cada cambio crea y aplica una nueva versión sólo para este tenant. Los leads existentes conservan la versión con la que comenzaron.
          </p>
          {loadingActivePayload ? (
            <p className='text-sm text-muted-foreground'>Cargando verticales…</p>
          ) : activeVerticals.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No hay verticales disponibles en la configuración activa.</p>
          ) : (
            <div className='grid gap-2 md:grid-cols-2'>
              {activeVerticals.map(vertical => (
                <label
                  key={vertical.key}
                  className='flex items-center justify-between gap-3 rounded-xl border p-3 text-sm'
                >
                  <span className='min-w-0'>
                    <span className='block font-medium'>{vertical.label}</span>
                    <span className='block truncate text-xs text-muted-foreground'>{vertical.key}</span>
                  </span>
                  <span className='flex items-center gap-2'>
                    <Badge variant={vertical.enabled ? 'default' : 'outline'}>
                      {vertical.enabled ? 'ON' : 'OFF'}
                    </Badge>
                    <input
                      type='checkbox'
                      aria-label={`Habilitar ${vertical.label}`}
                      checked={vertical.enabled}
                      disabled={Boolean(togglingVertical)}
                      onChange={event => void handleToggleVertical(vertical, event.target.checked)}
                    />
                  </span>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CampaignRoutingCard scopeKey={tenantId ?? ''} availableRoutes={campaignRoutes} />

      <Tabs defaultValue='wizard'>
        <TabsList>
          <TabsTrigger value='wizard'>Asistente visual</TabsTrigger>
          <TabsTrigger value='json'>JSON avanzado</TabsTrigger>
          <TabsTrigger value='versions'>Historial</TabsTrigger>
        </TabsList>

        <TabsContent value='wizard'>
          <FunnelConfigWizard
            configs={configs}
            onSaved={() => void loadConfigs()}
            onError={setError}
            onMessage={setMessage}
          />
        </TabsContent>

        <TabsContent value='json'>
          <Card className='rounded-2xl shadow-sm'>
            <CardHeader>
              <CardTitle>Importar JSON</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                Para cambios masivos o plantillas externas. La validación completa corre en el servidor al guardar.
              </p>
              <label className='flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-8 text-center transition-colors hover:bg-muted/40'>
                <Upload className='mb-2 h-8 w-8 text-muted-foreground' />
                <p className='font-medium'>Subir el JSON del embudo</p>
                <p className='text-xs text-muted-foreground'>Máximo 512 KB</p>
                <Input
                  type='file'
                  accept='application/json,.json'
                  className='sr-only'
                  onChange={event => void handleFile(event.target.files?.[0] ?? null)}
                />
              </label>

              <div className='space-y-2'>
                <Label htmlFor='funnel-json'>O pegá el JSON</Label>
                <Textarea
                  id='funnel-json'
                  value={jsonText}
                  onChange={event => setJsonText(event.target.value)}
                  rows={12}
                  className='font-mono text-xs'
                  placeholder='{ "schemaVersion": 2, ... }'
                />
                {parseError ? <p className='text-sm text-destructive'>{parseError}</p> : null}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='funnel-note'>Nota (opcional)</Label>
                <Input
                  id='funnel-note'
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  placeholder='Ej: setup informática MKT — ajuste de pesos'
                  maxLength={500}
                />
              </div>

              {preview ? (
                <div className='rounded-xl border bg-muted/30 p-4 text-sm'>
                  <p className='font-medium'>Vista previa</p>
                  <ul className='mt-2 space-y-1 text-muted-foreground'>
                    <li>schemaVersion: {preview.schemaVersion ?? '—'}</li>
                    <li>Verticals: {preview.verticalCount}</li>
                    <li>Cut de scoring: {preview.scoringCut ?? '—'}</li>
                    <li>Intake: {preview.intakeSources.length > 0 ? preview.intakeSources.join(', ') : '—'}</li>
                  </ul>
                </div>
              ) : null}

              <div className='flex flex-wrap gap-2'>
                <Button
                  type='button'
                  disabled={loading || Boolean(parseError) || !jsonText.trim()}
                  onClick={() => void handleImport(true)}
                >
                  {loading ? 'Procesando…' : 'Importar y aplicar ahora'}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  disabled={loading || Boolean(parseError) || !jsonText.trim()}
                  onClick={() => void handleImport(false)}
                >
                  Importar como borrador
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='versions'>
          <Card className='rounded-2xl shadow-sm'>
            <CardHeader>
              <CardTitle>Versiones guardadas</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              {configs.length === 0 ? (
                <p className='text-sm text-muted-foreground'>Todavía no hay versiones importadas.</p>
              ) : (
                configs.map(config => (
                  <div
                    key={config.id}
                    className='flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between'
                  >
                    <div className='space-y-1 text-sm'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='font-medium'>v{config.version}</span>
                        <Badge variant={statusVariant(config.status)}>{config.status}</Badge>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        Creada {formatDateTime24h(config.createdAt)}
                        {config.appliedAt
                          ? ` · Aplicada ${formatDateTime24h(config.appliedAt)}`
                          : ''}
                      </p>
                      {config.note ? <p className='text-muted-foreground'>{config.note}</p> : null}
                    </div>
                    {config.status !== 'ACTIVE' ? (
                      <Button
                        type='button'
                        size='sm'
                        variant={config.status === 'ARCHIVED' ? 'outline' : 'default'}
                        disabled={activatingId === config.id}
                        onClick={() => void handleActivate(config)}
                      >
                        {activatingId === config.id
                          ? 'Activando…'
                          : config.status === 'ARCHIVED'
                            ? 'Restaurar'
                            : 'Activar'}
                      </Button>
                    ) : null}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </ContactAgentPage>
  );
}
